/**
 * Download Utilities
 * 
 * This module provides centralized file download functionality for the application.
 * It includes generic download functions and submission-specific download handlers.
 * 
 * All download-related functions are centralized here to ensure consistency
 * across the application and make maintenance easier.
 */

import fileDownload from "js-file-download";
import Axios from "axios";

/**
 * Downloads a file from a URL and triggers the browser download
 * @param {string} fileUrl - The URL of the file to download
 * @param {string} fileName - The name to use for the downloaded file
 * @param {Function} onStart - Callback function called when download starts
 * @param {Function} onSuccess - Callback function called when download succeeds
 * @param {Function} onError - Callback function called when download fails
 * @returns {Promise<void>}
 */
export const downloadFile = async (fileUrl, fileName, onStart, onSuccess, onError) => {
  if (!fileUrl) {
    onError?.("No file URL provided");
    return;
  }

  try {
    onStart?.("Downloading file...");
    
    const fileDownloadRes = await Axios.get(fileUrl, {
      responseType: "arraybuffer",
    });
    
    fileDownload(fileDownloadRes.data, fileName);
    
    onSuccess?.("File downloaded successfully");
  } catch (err) {
    onError?.(`Error downloading file: ${err.message}`);
  }
};

/**
 * Downloads the original file from a submission record
 * @param {Object} submission - The submission record containing file information
 * @param {Function} onStart - Callback function called when download starts
 * @param {Function} onSuccess - Callback function called when download succeeds
 * @param {Function} onError - Callback function called when download fails
 * @returns {Promise<void>}
 */
export const downloadSubmissionOriginalFile = async (submission, onStart, onSuccess, onError) => {
  if (!submission?.partVectorSearch?.files?.original) {
    onError?.("No original file available for download");
    return;
  }

  const fileUrl = submission.partVectorSearch.files.original;
  const fileName = submission.partVectorSearch.fileName || "original-file";
  
  await downloadFile(fileUrl, fileName, onStart, onSuccess, onError);
}; 